import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast
} from '@chakra-ui/react'
import { getAllRules } from '../../api'
import CategoryDropDown from './CategoriesDropDown'
import { addRule } from '../../api/addRules'
import { deleteRule } from '../../api/deleteRule'
import { updateRule } from '../../api/updateRule'
import { FaPlus, FaSearch, FaTrash, FaEdit, FaFilter } from 'react-icons/fa'
import { LABEL, MESSAGE } from '../../../../constants'
import PaginatedDropdown from '../../../MainPage/components/DocumentProcessPage/PaginatedDropdown'
import FilterPopoverWithCheck from '../../../MainPage/components/DocumentTable/FilterPopoverWithCheck'
import { getCategories, getPartners, getPointOfWorks, getDecoratedPointOfWorks } from '../../api/getNomenclatures'
import getWCRShortName from '../../../../helpers/wcrCompany'
import ArticleDropDown from '../../../MainPage/components/DocumentProcessPage/ArticleDropDown'
import { getCompanies } from '../../../SettingsPage/api'

const RulesTable = () => {
  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [companies, setCompanies] = useState([])
  const [loadingCompany, setLoadingCompany] = useState(true)
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [pagination, setPagination] = useState({
    pageNumber: 0,
    size: 20,
    orderByList: [{ field: 'worldClassPartnerName', direction: 'ASCENDING' }]
  })
  const [totalPages, setTotalPages] = useState(1)

  const [selectedPartnerCifs, setSelectedPartnerCifs] = useState([])
  const [selectedPointOfWorks, setSelectedPointOfWorks] = useState([])
  const [selectedCategories, setSelectedCategories] = useState([])

  const [categoryOptions, setCategoryOptions] = useState([])
  useEffect(() => {
    async function fetchCategories() {
      try {
        const cats = await getCategories()
        setCategoryOptions(cats.map(item => item))
      } catch (error) {
        console.error('Error fetching categories:', error)
      }
    }
    fetchCategories()
  }, [])

  useEffect(() => {
      getCompanies()
        .then((response) => {
          setCompanies(response)
          setLoadingCompany(false)
        })
        .catch((error) => {
          console.error('Eroare la preluarea companiilor:', error)
          setLoadingCompany(false)
        })
    }, [])
  

  const [modalData, setModalData] = useState(null)

  const fetchRules = async () => {
    setLoading(true)
    try {
      const { pageNumber, size, orderByList } = pagination

      const response = await getAllRules({
        searchTerm: searchTerm || null,
        partnerCifs: selectedPartnerCifs.length ? selectedPartnerCifs.map((selected) => selected.cif) : null,
        pointOfWorks: selectedPointOfWorks.length ? selectedPointOfWorks.map((selected) => selected.name) : null,
        categories: selectedCategories.length ? selectedCategories : null,
        pageNumber,
        size,
        orderByList
      })

      if (response?.content) {
        setRules(response.content)
        const total = Math.ceil((response.total || 0) / size)
        setTotalPages(total)
      } else {
        setRules([])
        setTotalPages(1)
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: MESSAGE.RULES.errorFetch,
        status: 'error',
        duration: 5000,
        isClosable: true
      })
      console.error('Error fetching rules:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRules()
  }, [pagination, searchTerm, selectedPartnerCifs, selectedPointOfWorks, selectedCategories])

  const openAddModal = () => {
    setModalData({
      cif: '',
      worldClassPartnerName: '',
      pointOfWork: null,
      category: ''
    })
    onOpen()
  }

  const openUpdateModal = (rule) => {
    setModalData({
      id: rule.id,
      cif: rule.cif,
      worldClassPartnerName: rule.worldClassPartnerName,
      pointOfWork: rule.pointOfWork?.name || null,
      category: rule.category.name,
      worldClassPartnerCif: rule.worldClassPartnerCIF
    })
    onOpen()
  }

  const handleModalSubmit = async () => {
    if (!modalData) return

    if (modalData.id) {
      const payload = {
        id: modalData.id,
        cif: modalData.cif,
        partnerCIF: modalData.worldClassPartnerCif,
        pointOfWork: modalData.pointOfWork,
        category: modalData.category
      }
      try {
        await updateRule(payload)
        toast({
          title: 'Success',
          description: MESSAGE.RULES.ruleUpdateSuccess,
          status: 'success',
          duration: 5000,
          isClosable: true
        })
        setModalData(null)
        onClose()
        fetchRules()
      } catch (error) {
        console.error('Error updating rule:', error)
        toast({
          title: 'Error',
          description: error.response?.data?.message || MESSAGE.RULES.updateRuleError,
          status: 'error',
          duration: 5000,
          isClosable: true
        })
      }
    } else {
      const payload = {
        cif: modalData.cif,
        partnerCIF: modalData.worldClassPartnerName,
        pointOfWork: modalData.pointOfWork,
        category: modalData.category
      }
      try {
        await addRule(payload)
        toast({
          title: 'Success',
          description: MESSAGE.RULES.addRuleSuccess,
          status: 'success',
          duration: 5000,
          isClosable: true
        })
        setModalData(null)
        onClose()
        fetchRules()
      } catch (error) {
        console.error('Error adding rule:', error)
        toast({
          title: 'Error',
          description: error.response?.data?.message || MESSAGE.RULES.addRuleError,
          status: 'error',
          duration: 5000,
          isClosable: true
        })
      }
    }
  }

  const handleDeleteRule = async (id) => {
    try {
      await deleteRule(id)
      toast({
        title: 'Success',
        description: MESSAGE.RULES.deleteRuleSuccess,
        status: 'success',
        duration: 5000,
        isClosable: true
      })
      fetchRules()
    } catch (error) {
      toast({
        title: 'Error',
        description: MESSAGE.RULES.deleteRuleError,
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
  }

  const handleCategoryFilterChange = (category) => {
    setSelectedCategories((prev) => {
      if (prev.includes(category)) {
        return prev.filter((item) => item !== category)
      } else {
        return [...prev, category]
      }
    })
  }

  const renderRuleModal = () => (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setModalData(null)
        onClose()
      }}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{modalData?.id ? "Update Regula" : LABEL.RULES_TABLE.title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb="3">
            <FormLabel textColor="gray.500" fontSize="sm" mb="1">
                {LABEL.ARTICLE_RULES_TABLE.company}
              </FormLabel>
              <ArticleDropDown
                value={modalData?.cif || ''}
                onChange={newCif => {
                  setModalData(prev => ({ ...prev, cif: newCif }));
                }}
                articles={companies}
                loading={loadingCompany}
              />
            <FormLabel textColor="gray.500" mb="0" fontSize="sm">
              {LABEL.PARTNER.label}
            </FormLabel>
            <PaginatedDropdown
              key={modalData?.cif}
              value={modalData?.worldClassPartnerName || ''}
              onChange={(selectedName) =>
                setModalData((prev) => ({
                  ...prev,
                  worldClassPartnerName: selectedName
                }))
              }
              label="Selecteaza partener"
              placeholder="Selecteaza partener"
              size={15}
              fetchData={getPartners}
              attribute={"cif"}
              isPartner={true}
              cifValue={modalData?.cif}
            />
            <FormLabel textColor="gray.500" mb="0" fontSize="sm">
              {LABEL.POW_DROPDOWN.label}
            </FormLabel>
            <PaginatedDropdown
              key={modalData?.cif[1]}
              value={modalData?.pointOfWork ?? null}
              fetchData={getPointOfWorks}
              onChange={(selectedName) =>
                setModalData((prev) => ({
                  ...prev,
                  pointOfWork: selectedName
                }))
              }
              label={LABEL.POW_DROPDOWN.label}
              placeholder={LABEL.POW_DROPDOWN.placeholder}
              cifValue={modalData?.cif}
              nullDefault={true}
            />
            <CategoryDropDown
              value={modalData?.category || ''}
              onChange={(e) =>
                setModalData((prev) => ({
                  ...prev,
                  category: e
                }))
              }
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" onClick={handleModalSubmit}>
            {modalData?.id ? "Update Regula" : LABEL.RULES_TABLE.addRuleButton}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )

  const renderTable = () => {
    if (loading) {
      return (
        <Box textAlign="center" p="4">
          <Spinner size="lg" />
        </Box>
      )
    }

    return (
      <Table variant="simple" size="sm" sx={{ fontSize: 'sm', th: { p: 0.5 }, td: { p: 0.5 } }}>
        <Thead>
          <Tr>
            <Th>{LABEL.RULES_TABLE.index}</Th>
            <Th>{LABEL.RULES_TABLE.organisation}</Th>
            <Th>
              <Flex align="center">
                <Text mr="2">{LABEL.RULES_TABLE.partnerName}</Text>
                <FilterPopoverWithCheck
                  initialSelected={selectedPartnerCifs}
                  onSubmit={(selected) => setSelectedPartnerCifs(selected)}
                  fetchItems={getPartners}
                  content={true}
                />
              </Flex>
            </Th>
            <Th>
              <Flex align="center">
                <Text mr="2">{LABEL.RULES_TABLE.pointOfWork}</Text>
                <FilterPopoverWithCheck
                  initialSelected={selectedPointOfWorks}
                  onSubmit={(selected) => setSelectedPointOfWorks(selected)}
                  fetchItems={getDecoratedPointOfWorks}
                  content={true}
                  isScrollable={true}
                />
              </Flex>
            </Th>
            <Th>
              <Flex align="center">
                <Text mr="2">{LABEL.RULES_TABLE.category}</Text>
                <Popover>
                  <PopoverTrigger>
                    <Button size="sm" ml="2" variant="ghost">
                      <Icon
                        as={FaFilter}
                        color={selectedCategories.length > 0 ? 'blue.500' : 'gray.500'}
                      />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent>
                    <PopoverArrow />
                    <PopoverCloseButton />
                    <PopoverHeader>{LABEL.RULES_TABLE.categoryFilter}</PopoverHeader>
                    <PopoverBody>
                      {categoryOptions.map((cat) => (
                        <Flex key={cat} align="center" mb={2}>
                          <Checkbox
                            isChecked={selectedCategories.includes(cat)}
                            onChange={() => handleCategoryFilterChange(cat)}
                          >
                            {cat}
                          </Checkbox>
                        </Flex>
                      ))}
                      <Button mt="2" size="sm" colorScheme="blue" onClick={() => setSelectedCategories([])}>
                        {LABEL.PROCESS_TABLE_HEAD.deleteFilters}
                      </Button>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              </Flex>
            </Th>
            <Th>{LABEL.RULES_TABLE.actions}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rules.length === 0 ? (
            <Tr>
              <Td colSpan="5" textAlign="center">
                {LABEL.EMPTY_FILTER.label}
              </Td>
            </Tr>
          ) : (
            rules.map((rule, index) => (
              <Tr key={rule.id}>
                <Td borderRadius="md" border="1px solid" borderColor="gray.200">
                  {pagination.pageNumber * pagination.size + index + 1}
                </Td>
                <Td>{getWCRShortName(rule.cif) || rule.cif}</Td>
                <Td>{rule.worldClassPartnerName || '-'}</Td>
                <Td>{rule.pointOfWork?.name || '-'}</Td>
                <Td>{rule.category?.name || '-'}</Td>
                <Td>
                  <Flex gap="2">
                  <Button
                    ml="2"
                    colorScheme="green"
                    size="sm"
                    variant="outline"
                    _hover={{ bg: 'green.500', color: 'white' }}
                    onClick={() => openUpdateModal(rule)}
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    colorScheme="red"
                    size="sm"
                    variant="outline"
                    _hover={{ bg: 'red.500', color: 'white' }}
                    onClick={() => handleDeleteRule(rule.id)}
                  >
                    <FaTrash />
                  </Button>
                  </Flex>
                </Td>
              </Tr>
            ))
          )}
        </Tbody>
      </Table>
    )
  }

  return (
    <>
      <Flex align="center" justifyContent="space-between" width="100%" mb="5">
        <InputGroup>
          <InputLeftElement color="gray.500" pointerEvents="none">
            <FaSearch />
          </InputLeftElement>
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={LABEL.SEARCH_TERM.placeholder}
          />
        </InputGroup>
        <Button ml="5" colorScheme="blue" onClick={openAddModal} leftIcon={<FaPlus />}>
          {LABEL.RULES_TABLE.addRuleButton}
        </Button>
      </Flex>
      {renderTable()}
      <Flex justify="space-between" mt="4" align="center">
        <Button
          onClick={() =>
            setPagination((prev) => ({
              ...prev,
              pageNumber: Math.max(prev.pageNumber - 1, 0)
            }))
          }
          isDisabled={pagination.pageNumber === 0}
          colorScheme="blue"
          size="sm"
        >
          {LABEL.PREVIOUS_PAGE.label}
        </Button>
        <Text>
          Page {pagination.pageNumber + 1} / {totalPages}
        </Text>
        <Button
          onClick={() =>
            setPagination((prev) => ({
              ...prev,
              pageNumber: Math.min(prev.pageNumber + 1, totalPages - 1)
            }))
          }
          isDisabled={pagination.pageNumber + 1 >= totalPages}
          colorScheme="blue"
          size="sm"
        >
          {LABEL.NEXT_PAGE.label}
        </Button>
      </Flex>
      {renderRuleModal()}
    </>
  )
}

export default RulesTable
